'use client'

/**
 * The filter finder (FRIDGE-723) — one box, two kinds of answer.
 *
 * Type the model number from the data label inside the refrigerator, or the
 * code printed on the current filter. The form calls /api/filters/search (the
 * one finder endpoint) and renders whichever answer the resolver returns:
 *
 *   fridge mode      the matched appliance, then every filter the sourced
 *                    compatibility rows say fits it — OEM first, then
 *                    compatible replacements cheapest-first;
 *   filter-code mode the code's identity (brand, name, what it replaces, what
 *                    replaced it), the per-model hub when it serves, then the
 *                    family's live rows in the same order.
 *
 * Real-or-Hidden throughout: a fridge with no compatibility rows says so and
 * offers the brand guides; an unknown query says so too. Prices render only
 * when a live offer exists (formatCents returns '' for 0). Every filter link
 * is the row's `path` from the resolver — /filters/<slug> via lib/parts/fpn —
 * never a literal root.
 *
 * Mounted on /filters (id="filter-finder", the html-render-gate marker) and
 * reusable on any filter surface via `variant="compact"`.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react'
import Link from 'next/link'
import { Search } from 'lucide-react'
import { filterCoverageNote, filterFitmentNote } from '@/lib/filters/fitment-evidence'
import { FilterApplianceChoices } from '@/components/filters/filter-appliance-choices'
import { FilterOptionCard } from '@/components/filters/filter-card'
import { logoFor } from '@/lib/filters/brand-logos'
import { getFilterHubSlugForBrand } from '@/lib/filter-hub-slugs'
import { FILTERS_ROUTE_ROOT } from '@/lib/parts/fpn'
import type { FinderResult } from '@/lib/filters/finder'

const FALLBACK_BRANDS = ['Samsung', 'Whirlpool', 'LG', 'GE', 'Frigidaire', 'KitchenAid'] as const

interface Props {
  variant?: 'hero' | 'compact'
  /** Prefill and run once on mount (deep links). */
  initialQuery?: string
  className?: string
}

export function FilterFinder({ variant = 'hero', initialQuery, className = '' }: Props) {
  const [query, setQuery] = useState(initialQuery ?? '')
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<FinderResult | null>(null)
  const [failed, setFailed] = useState(false)
  const inFlight = useRef<AbortController | null>(null)

  async function run(term: string) {
    const raw = term.trim()
    if (raw.length < 3) return
    inFlight.current?.abort()
    const ctrl = new AbortController()
    inFlight.current = ctrl
    setLoading(true)
    setFailed(false)
    try {
      const res = await fetch(`/api/filters/search?q=${encodeURIComponent(raw)}`, { signal: ctrl.signal })
      if (!res.ok) throw new Error(String(res.status))
      const data = (await res.json()) as FinderResult
      setResult(data)
    } catch (e) {
      if ((e as { name?: string }).name === 'AbortError') return
      setResult(null)
      setFailed(true)
    } finally {
      if (inFlight.current === ctrl) setLoading(false)
    }
  }

  // A deep-linked query runs inside an async block owned by the effect — the
  // same idiom the tool page uses; the rule accepts setState there.
  useEffect(() => {
    if (!initialQuery || initialQuery.trim().length < 3) return
    void (async () => {
      await run(initialQuery)
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    void run(query)
  }

  const hero = variant === 'hero'
  const showResults = result !== null || failed

  return (
    <section
      id="filter-finder"
      className={`scroll-mt-[110px] ${hero ? 'rounded-2xl border border-gray-900 bg-white p-6 sm:p-8' : 'rounded-xl border border-gray-200 bg-white p-5'} ${className}`}
      aria-labelledby="filter-finder-heading"
    >
      <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#ff9951] mb-2">Filter finder</p>
      <h2 id="filter-finder-heading" className={`font-black tracking-tight text-gray-900 ${hero ? 'text-2xl sm:text-3xl' : 'text-xl'}`}>
        Enter your refrigerator model or your filter number
      </h2>
      <p className="text-sm text-gray-600 mt-2 max-w-2xl leading-relaxed">
        The model number is on the data label inside the fresh-food compartment; the filter number is printed on the
        cartridge you are replacing. Either one works — for example <span className="font-mono">LRFXC2606S</span> or{' '}
        <span className="font-mono">DA29-00020B</span>.{' '}
        <a href="#model-number-locator" className="text-[#0469f0] font-semibold hover:underline">
          Where is my model number?
        </a>
      </p>

      <form onSubmit={onSubmit} className="mt-5 flex flex-col sm:flex-row gap-3" role="search" aria-label="Find a refrigerator filter">
        {/* The accessible name rides on the input itself (aria-label), the same
            shape the homepage search box uses. A visually-hidden <label> beside
            it named the field twice and tripped the label-has-for ratchet. */}
        <input
          id="filter-finder-input"
          aria-label="Refrigerator model number or filter part number"
          name="q"
          type="text"
          inputMode="text"
          autoComplete="off"
          spellCheck={false}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Model number or filter number"
          maxLength={64}
          className="flex-1 rounded-lg border border-gray-300 px-4 py-3.5 text-base font-semibold text-gray-900 outline-none focus:border-[#ff9951] focus:ring-2 focus:ring-[#ff9951]/30"
          style={{ fontVariantNumeric: 'tabular-nums' }}
        />
        <button
          type="submit"
          disabled={loading || query.trim().length < 3}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#ff9951] px-6 py-3.5 text-sm font-bold uppercase tracking-[0.08em] text-[var(--color-brand-action-text)] transition-colors hover:bg-[#ff8533] disabled:cursor-not-allowed disabled:opacity-100"
        >
          <Search className="h-4 w-4" aria-hidden="true" />
          {loading ? 'Searching…' : 'Find my filter'}
        </button>
      </form>

      {/* Results live region — present before content changes so screen readers announce it. */}
      <div role="status" aria-live="polite" className="mt-6 space-y-5" hidden={!showResults}>
        {failed && (
          <p className="text-sm text-gray-700">
            The lookup could not be completed just now. Try again, or browse the filter guides by brand below.
          </p>
        )}

        {result?.applianceMatch?.status === 'ambiguous' && (
          <FilterApplianceChoices candidates={result.fridgeCandidates ?? []} truncated={result.applianceMatch.candidatesTruncated} />
        )}
        {result?.applianceMatch?.status === 'candidate' && (
          <p className="text-sm text-gray-700">Candidate only: confirm the complete model on your appliance label before choosing any filter shown below.</p>
        )}

        {result?.mode === 'fridge' && result.fridge && <p className="text-sm text-gray-700">{filterCoverageNote(result.compatibilityCoverage)}</p>}

        {result?.mode === 'fridge' && result.fridge && (
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
            <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-gray-500 mb-1">{result.applianceMatch?.status === 'candidate' ? 'Candidate refrigerator' : 'Matched refrigerator'}</p>
            {result.fridge.path ? (
              <Link href={result.fridge.path} className="text-base font-bold text-[#0469f0] hover:underline">
                {result.fridge.title}
              </Link>
            ) : (
              <p className="text-base font-bold text-gray-900">{result.fridge.title}</p>
            )}
            {result.fridge.model && (
              <p className="text-xs text-gray-600 mt-1">
                Model <span className="font-mono">{result.fridge.model}</span>
              </p>
            )}
          </div>
        )}

        {result?.mode === 'filter-code' && result.model && (
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
            <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-gray-500 mb-1">Matched filter number</p>
            <p className="text-base font-bold text-gray-900">
              {result.model.brand && <span className="text-[#0469f0]">{result.model.brand}{' '}</span>}
              <span className="font-mono">{result.model.code}</span>
              {result.model.name ? ` — ${result.model.name}` : ''}
            </p>
            {result.model.note && <p className="text-xs text-gray-600 mt-1">{result.model.note}</p>}
            {result.model.replaces.length > 0 && (
              <p className="text-xs text-gray-600 mt-1">
                Replaces <span className="font-mono">{result.model.replaces.join(', ')}</span>
              </p>
            )}
            {result.model.replacedBy.length > 0 && (
              <p className="text-xs text-gray-600 mt-1">
                Superseded by <span className="font-mono">{result.model.replacedBy.join(', ')}</span>
              </p>
            )}
            {result.model.hubPath && (
              <Link href={result.model.hubPath} className="inline-block mt-2 link-strong">
                Every {result.model.code} option, prices compared →
              </Link>
            )}
          </div>
        )}

        {result && result.filters.length > 0 && (
          <div>
            <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
              <h3 className="text-base font-black tracking-tight text-gray-900">
                {result.mode === 'fridge' ? (result.applianceMatch?.status === 'candidate' ? 'Filters for the candidate model' : 'Filters for this model') : 'Filters in this family'}
              </h3>
              <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-gray-500">
                {result.filters.length} {result.filters.length === 1 ? 'match' : 'matches'}
              </span>
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {result.filters.map((f) => (
                <li key={f.id} className="h-full">
                  {/* FRIDGE-1088 — the shared filter card: photo (or the
                      designed brand tile), brand logo, part number, FFN chip,
                      and the genuine / compatible pill. The link is the
                      resolver's path, never a literal root. */}
                  <FilterOptionCard
                    f={{
                      href: f.path,
                      title: f.title,
                      brand: f.brand,
                      model: f.model,
                      ffn: f.ffn,
                      cents: f.lowest_price_cents,
                      image: f.images[0] ?? null,
                      genuine: f.is_oem_match,
                    }}
                    logo={logoFor(result.brandLogos ?? {}, f.brand)}
                    showKind
                    evidenceNote={result.fridge ? filterFitmentNote(f.fitmentEvidence) : undefined}
                  />
                </li>
              ))}
            </ul>
          </div>
        )}

        {result && result.mode === 'fridge' && result.fridge && result.filters.length === 0 && (
          <NoMatch
            title={result.applianceMatch?.status === 'candidate' ? "We found a candidate model, but no displayable filter matches for it." : "We found your refrigerator, but no displayable filter matches for it."}
            body="An empty result does not mean no filter fits. Confirm the full model and existing filter number with the manufacturer, or start from the brand guide:"
          />
        )}
        {result && result.mode === 'none' && (
          <NoMatch
            title={`No refrigerator or filter matched “${result.query}”.`}
            body="Try the model number alone, with no extra characters — or start from the brand guide:"
          />
        )}
      </div>
    </section>
  )
}

function NoMatch({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <p className="text-sm text-gray-800">{title}</p>
      <p className="mt-1 text-xs text-gray-500">{body}</p>
      <ul className="mt-3 flex flex-wrap gap-2">
        {FALLBACK_BRANDS.map((b) => {
          const slug = getFilterHubSlugForBrand(b)
          return slug ? (
            <li key={b}>
              <Link
                href={`${FILTERS_ROUTE_ROOT}/${slug}`}
                className="block rounded-full border border-[#0469f0]/20 px-3 py-1.5 text-xs text-[#0469f0] hover:bg-[#0469f0]/5"
              >
                {b} filters
              </Link>
            </li>
          ) : null
        })}
      </ul>
    </div>
  )
}
